import { useEffect, useState } from "react";
import { CoverImg } from "@/components/ui/CoverImg";
import { IconHeart, IconHeartFilled, IconMore, IconPause } from "@/components/ui/icons";
import { AddToPlaylistModal } from "@/components/playlist/AddToPlaylistModal";
import { useContextMenu } from "@/hooks/useContextMenu";
import { useLibrary } from "@/hooks/useLibrary";
import { usePlayer } from "@/hooks/usePlayer";
import { fmtDuration } from "@/utils/format";
import { getAccess } from "@/lib/api";
import { isOffline, removeTrack, saveTrack } from "@/lib/offline";
import { sfx } from "@/lib/sfx";
import type { Track } from "@/types/types";

interface P { track: Track; index: number; queue: Track[] }

export function TrackRow({ track, index, queue }: P) {
  const p = usePlayer();
  const { isLiked, toggle } = useLibrary();
  const menu = useContextMenu();
  const liked = isLiked("track", track.id);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [heartBoom, setHeartBoom] = useState(0);

  // Is this row the track currently loaded in the player? Playing vs. merely
  // loaded-and-paused get two different indicators in the index column.
  const isCurrent = p.current?.id === track.id;
  const isPlayingThis = isCurrent && p.playing;

  const [saved, setSaved] = useState<boolean>(() => isOffline(track.id));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setSaved(isOffline(track.id)); }, [track.id]);

  const doLike = () => {
    if (!liked) sfx.chirp(); else sfx.tap();
    setHeartBoom((n) => n + 1);
    void toggle("track", track.id);
  };

  const doOffline = async () => {
    sfx.tap();
    if (saved) { await removeTrack(track.id); setSaved(false); return; }
    setSaving(true);
    const ok = await saveTrack(track.id, getAccess());
    setSaving(false);
    if (ok) { sfx.chirp(); setSaved(true); }
  };

  const menuItems = [
    { label: "Lecture", onSelect: () => { sfx.pop(); void p.play(queue, index); } },
    { label: "Ajouter à la file", onSelect: () => { sfx.tap(); p.enqueue(track); } },
    { divider: true },
    { label: "Ajouter à une playlist…", onSelect: () => { sfx.swoosh(); setPickerOpen(true); } },
    { label: liked ? "Retirer des likés" : "Ajouter aux likés", onSelect: doLike },
    { label: saved ? "Retirer du hors-ligne" : "Télécharger pour hors-ligne",
      onSelect: () => void doOffline() },
  ];

  return (
    <>
      <div
        className={"row" + (isCurrent ? " row-playing" : "")}
        onDoubleClick={() => { sfx.pop(); void p.play(queue, index); }}
        onContextMenu={(e) => { e.preventDefault(); menu.show({ x: e.clientX, y: e.clientY, items: menuItems }); }}
        style={{ padding: "6px 10px", borderRadius: 8, gap: 12, minWidth: 0 }}
      >
        <span
          className="muted"
          style={{
            width: 24, flexShrink: 0,
            display: "inline-flex", alignItems: "center", justifyContent: "flex-end",
            color: isCurrent ? "var(--accent)" : undefined,
          }}
        >
          {isPlayingThis ? (
            // Animated equalizer — echoes the favicon's 3-bar motif.
            <span className="eq-bars" aria-hidden="true"><span /><span /><span /></span>
          ) : isCurrent ? (
            <IconPause size={14} />
          ) : (
            index + 1
          )}
        </span>
        <CoverImg
          coverId={track.album?.cover_id}
          alt={track.title}
          size={40}
          className="cover"
        />
        <button
          className="grow"
          style={{ textAlign: "left", minWidth: 0, overflow: "hidden" }}
          onClick={() => { sfx.pop(); void p.play(queue, index); }}
        >
          <div className="trunc" style={{ color: isCurrent ? "var(--accent)" : undefined }}>
            {track.title}
          </div>
          <div className="muted trunc">{track.artist.name}</div>
        </button>
        <button title="Ajouter à une playlist" onClick={() => { sfx.tap(); setPickerOpen(true); }}
                style={{ fontSize: 18, lineHeight: 1, padding: "2px 8px",
                         border: "1px solid var(--line)", borderRadius: 999,
                         flexShrink: 0 }}>+</button>
        <button onClick={doLike} style={{ flexShrink: 0 }}>
          <span key={heartBoom} className={heartBoom ? "heart-bloom" : ""}
                style={{ display: "inline-flex", color: liked ? "var(--accent)" : "inherit" }}>
            {liked ? <IconHeartFilled /> : <IconHeart />}
          </span>
        </button>
        <span className="muted" style={{ minWidth: 46, textAlign: "right", flexShrink: 0 }}>
          {fmtDuration(track.duration_ms)}
        </span>
        <button title="Plus" onClick={(e) => menu.show({ x: e.clientX, y: e.clientY, items: menuItems })}
                style={{ flexShrink: 0 }}>
          <IconMore />
        </button>
      </div>
      <AddToPlaylistModal open={pickerOpen} onClose={() => setPickerOpen(false)} trackId={track.id} />
    </>
  );
}
